import {
  ChartCandlestick,
  ChartLine,
  House,
  Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { CSSProperties, MouseEvent } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useT } from '../../i18n'
import type { Translations } from '../../i18n'
import { tabOwnsPath } from '../../lib/tabs'
import styles from './BottomNav.module.css'

interface Tab {
  to: string
  /** Read from the dictionary at render time, never stored as a literal. */
  label: (t: Translations) => string
  icon: LucideIcon
}

/**
 * Four tabs. Signal codes are entered in Trade → Copy trading since the
 * 28 September call, so the bar no longer carries a tab of their own.
 */
const TABS: readonly Tab[] = [
  { to: '/', label: (t) => t.nav.home, icon: House },
  { to: '/markets', label: (t) => t.nav.markets, icon: ChartLine },
  { to: '/trade', label: (t) => t.nav.trade, icon: ChartCandlestick },
  { to: '/wallet', label: (t) => t.nav.wallet, icon: Wallet },
]

/** Takes the list of the screen on show back to its top. */
function scrollListToTop(): void {
  const list = document.querySelector('main')
  if (list === null) return
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  list.scrollTo({ top: 0, behavior: still ? 'auto' : 'smooth' })
}

/**
 * The floating glass tab bar (Figma "Tab Bar"): a 64 px pill over a fade,
 * four equal cells, icon 24 over an 11/13 label. The current destination is
 * the accent, icon and label together, on a capsule that slides across when
 * the tab changes; NavLink sets aria-current="page" on it. A tap on the tab
 * already open takes its list back to the top instead of reloading it, the
 * platform's convention.
 */
export function BottomNav(): JSX.Element {
  const t = useT()
  const { pathname } = useLocation()
  const current = TABS.findIndex((tab) => tabOwnsPath(tab.to, pathname))

  const handleClick = (to: string) => (event: MouseEvent<HTMLAnchorElement>): void => {
    if (pathname !== to) return
    event.preventDefault()
    scrollListToTop()
  }

  return (
    <div className={styles.dock}>
      <nav
        className={styles.bar}
        aria-label={t.nav.label}
        style={{ '--tab-count': TABS.length, '--tab-index': Math.max(current, 0) } as CSSProperties}
      >
        <span className={styles.selection} data-idle={current < 0 ? '' : undefined} aria-hidden="true" />
        {TABS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            onClick={handleClick(to)}
            className={({ isActive }) =>
              [styles.tab, isActive ? styles.active : ''].filter(Boolean).join(' ')
            }
          >
            <span className={styles.icon} aria-hidden="true">
              <Icon size={24} strokeWidth={1.5} fill="none" />
            </span>
            <span className={styles.label}>{label(t)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
